/**
 * Bezorgdag per postcoderegio — afgeleid uit de Timmers-leverroutekaart
 * "Postcode_gebieden" (versie 24-03-2025). Timmers' eigen glastransport rijdt elke
 * 2-cijferige postcoderegio (PC2) op een vaste weekdag aan vanuit depot Oosterhout.
 *
 * Samen met de levertijd (productietijd) maakt dit de leverbelofte concreter:
 * "in jouw regio bezorgen we meestal op woensdag → rond wo 16 jul".
 *
 * BELANGRIJK — dit is een INDICATIE:
 *  - Geldt alleen voor eigen transport (glas op maat). Pakketten via Transmission
 *    volgen deze route niet.
 *  - De mapping is overgenomen van de kleurenkaart; randgebieden kunnen een dag
 *    afwijken. Controleer bij twijfel met Timmers (0162-461175) en pas de tabel
 *    hieronder aan — dit is de enige plek waar de regio→dag-data leeft.
 *  - Niet in de tabel verwerkte route-uitzonderingen van de kaart:
 *      · Bussum en Naarden (begin PC 14) → altijd dinsdagroute.
 *      · PC-regio 13 → let op: zowel dinsdag- als woensdagroute.
 *    Voor de klant-indicatie houden we de hoofdkleur aan (14 = wo, 13 = wo).
 */

/** JS getDay()-index per weekdag (zo=0 … za=6). */
export const enum RouteDay {
  Maandag = 1,
  Dinsdag = 2,
  Woensdag = 3,
  Donderdag = 4,
  Vrijdag = 5,
}

const DAY_NAME: Record<number, string> = {
  1: "maandag",
  2: "dinsdag",
  3: "woensdag",
  4: "donderdag",
  5: "vrijdag",
};

/** Bouwt de PC2→dag-lookup uit per-dag lijsten (leesbaar én makkelijk te corrigeren). */
function buildMap(): Record<string, number> {
  const groups: Record<number, number[]> = {
    [RouteDay.Maandag]: [43, 44, 45, 46],
    [RouteDay.Dinsdag]: [77, 78, 79, 80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97, 98, 99],
    [RouteDay.Woensdag]: [10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 33],
    [RouteDay.Donderdag]: [32, 34, 35, 36, 37, 38, 39, 40, 41, 42, 52, 53, 54, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76],
    [RouteDay.Vrijdag]: [47, 48, 49, 50, 51, 55, 56, 57, 58, 59, 60, 61, 62, 63, 64],
  };
  const map: Record<string, number> = {};
  for (const [day, pcs] of Object.entries(groups)) {
    for (const pc of pcs) map[String(pc)] = Number(day);
  }
  return map;
}

const DELIVERY_DAY_BY_PC2 = buildMap();

/** Waddeneilanden — geen eigen-transportlevering (los van de PC2-route). PC4-bereiken. */
function isWaddenIsland(pc4: number): boolean {
  return (
    (pc4 >= 1790 && pc4 <= 1799) || // Texel
    pc4 === 8899 ||                 // Vlieland
    (pc4 >= 8881 && pc4 <= 8897) || // Terschelling
    (pc4 >= 9161 && pc4 <= 9164) || // Ameland
    pc4 === 9166                    // Schiermonnikoog
  );
}

/** Haalt de 4 cijfers uit een (vrij ingevoerde) NL-postcode, of null. */
function pc4Of(postcode: string): number | null {
  const m = postcode.replace(/\s+/g, "").match(/^(\d{4})/);
  return m ? Number(m[1]) : null;
}

export interface RegionDelivery {
  /** getDay()-index (1=ma … 5=vr) van de vaste bezorgdag, of null bij geen levering. */
  dayIndex: number | null;
  /** Nederlandse weekdagnaam ("woensdag"), of null. */
  dayName: string | null;
  /** True voor de Waddeneilanden — geen eigen-transportlevering. */
  island: boolean;
}

/**
 * Bepaalt de vaste bezorgdag voor een NL-postcode op basis van de Timmers-route.
 * Geeft null terug als de postcode niet (nog) herkend wordt.
 */
export function deliveryRegion(postcode: string | undefined): RegionDelivery | null {
  if (!postcode) return null;
  const pc4 = pc4Of(postcode);
  if (pc4 == null) return null;
  if (isWaddenIsland(pc4)) return { dayIndex: null, dayName: null, island: true };
  const pc2 = String(Math.floor(pc4 / 100));
  const dayIndex = DELIVERY_DAY_BY_PC2[pc2];
  if (dayIndex == null) return null;
  return { dayIndex, dayName: DAY_NAME[dayIndex] ?? null, island: false };
}
